/**
 * Application configuration
 */

export interface AppConfig {
  api: {
    baseUrl: string;
    timeout: number;
    retryAttempts: number;
  };
  auth: {
    tokenKey: string;
    refreshTokenKey: string;
    tokenExpiration: number;
  };
  persian: {
    defaultDirection: 'rtl' | 'ltr' | 'auto';
    enableDigitConversion: boolean;
    enableTextNormalization: boolean;
    fontLoadTimeout: number;
  };
  audio: {
    maxFileSize: number; // in bytes
    allowedFormats: string[];
    recordingTimeout: number; // in seconds
  };
  ui: {
    animationDuration: number;
    toastDuration: number;
    debounceDelay: number;
  };
  development: {
    enableMockApi: boolean;
    enableDebugLogs: boolean;
  };
}

const config: AppConfig = {
  api: {
    baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:5127',
    timeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '10000', 10),
    retryAttempts: parseInt(import.meta.env.VITE_API_RETRY_ATTEMPTS || '3', 10),
  },
  
  auth: {
    tokenKey: 'persian_learning_token',
    refreshTokenKey: 'persian_learning_refresh_token',
    tokenExpiration: 24 * 60 * 60 * 1000, // 24 hours in milliseconds
  },
  
  persian: {
    defaultDirection: 'auto',
    enableDigitConversion: true,
    enableTextNormalization: true,
    fontLoadTimeout: 3000,
  },
  
  audio: {
    maxFileSize: 10 * 1024 * 1024, // 10MB
    allowedFormats: ['audio/wav', 'audio/mp3', 'audio/ogg', 'audio/webm'],
    recordingTimeout: 300, // 5 minutes
  },
  
  ui: {
    animationDuration: 300,
    toastDuration: 4000,
    debounceDelay: 300,
  },
  
  development: {
    enableMockApi: import.meta.env.VITE_ENABLE_MOCK_API === 'true',
    enableDebugLogs: import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === 'true',
  },
};

export default config;

// Environment-specific configurations
export const isDevelopment = import.meta.env.DEV;
export const isProduction = import.meta.env.PROD;
export const isTest = import.meta.env.MODE === 'test';

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  auth: {
    login: '/api/Auth/login',
    register: '/api/Auth/register',
    logout: '/api/Auth/logout',
    profile: '/api/Auth/profile',
    updateProfile: '/api/Auth/profile',
    changePassword: '/api/Auth/change-password',
  },
  
  // Vocabulary
  vocabulary: {
    list: '/api/Vocabulary',
    byId: (id: number) => `/api/Vocabulary/${id}`,
    categories: '/api/Vocabulary/categories',
    markLearned: (id: number) => `/api/Vocabulary/${id}/mark-learned`,
    exercises: {
      matching: '/api/Vocabulary/exercises/matching',
      flashcards: '/api/Vocabulary/exercises/flashcards',
      submit: '/api/Vocabulary/exercises/submit',
    },
  },
  
  // Grammar
  grammar: {
    list: '/api/Grammar',
    byId: (id: number) => `/api/Grammar/${id}`,
    exercise: (lessonId: number) => `/api/Grammar/${lessonId}/exercise`,
    submitExercise: '/api/Grammar/exercise/submit',
    complete: (id: number) => `/api/Grammar/${id}/complete`,
    progress: '/api/Grammar/progress',
  },
  
  // Dialogue
  dialogue: {
    list: '/api/Dialogue',
    start: (id: number) => `/api/Dialogue/${id}/start`,
    respond: '/api/Dialogue/respond',
    complete: (id: number) => `/api/Dialogue/${id}/complete`,
    progress: (id: number) => `/api/Dialogue/${id}/progress`,
    stats: '/api/Dialogue/stats',
  },
  
  // Quiz
  quiz: {
    list: '/api/Quiz',
    generate: '/api/Quiz/generate',
    submit: '/api/Quiz/submit',
    results: '/api/Quiz/results',
    stats: '/api/Quiz/stats',
    levelAdvancement: '/api/Quiz/level-advancement',
  },
  
  // Audio
  audio: {
    pronunciation: (level: number) => `/api/Audio/pronunciation/${level}`,
    upload: '/api/Audio/upload',
    recordings: '/api/Audio/recordings',
    deleteRecording: (recordingId: string) => `/api/Audio/recordings/${recordingId}`,
    validate: '/api/Audio/validate',
    metadata: (fileName: string) => `/api/Audio/metadata/${fileName}`,
  },
  
  // Level Progression
  levelProgression: {
    status: '/api/LevelProgression/status',
    evaluate: '/api/LevelProgression/evaluate',
    promote: '/api/LevelProgression/promote',
    content: '/api/LevelProgression/content',
    stats: '/api/LevelProgression/stats',
    criteria: (level: number) => `/api/LevelProgression/criteria/${level}`,
  },
  
  // Dashboard
  dashboard: {
    main: '/api/Dashboard',
    stats: '/api/Dashboard/stats',
  },
  
  // Admin
  admin: {
    seed: {
      all: '/api/Seed/all',
      vocabulary: '/api/Seed/vocabulary',
      grammar: '/api/Seed/grammar',
      dialogue: '/api/Seed/dialogue',
      quiz: '/api/Seed/quiz',
    },
    performance: {
      metrics: '/api/Performance/metrics',
      health: '/api/Performance/health',
    },
  },
  
  // Health
  health: '/api/Performance/health',
  
  // Performance
  performance: {
    metrics: '/api/Performance/metrics',
    health: '/api/Performance/health',
  },
} as const;

// Feature flags
export const FEATURES = {
  enableAudioRecording: true,
  enableOfflineMode: false,
  enablePushNotifications: true,
  enableAnalytics: isProduction,
  enableErrorReporting: isProduction,
} as const;

// Persian language configuration
export const PERSIAN_CONFIG = {
  fonts: {
    primary: 'Vazirmatn',
    secondary: 'Noto Sans Arabic',
    fallback: 'Tahoma, Arial Unicode MS, sans-serif',
  },
  
  rtlLanguages: ['fa', 'ar', 'he', 'ur'],
  
  digitConversion: {
    persian: ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'],
    arabic: ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'],
    english: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'],
  },
} as const;